import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { addEntryDraftInput } from './account-entry';
import { BANK_ACCOUNT_TAB_ACTIONS } from './bank-account-tabs.screen';
import { BENEFICIARY_LIMIT_ACTIONS } from './beneficiary-limits.screen';
import { PAYMENT_ACTIONS, transitoryPaymentsPage } from './payment.screen';
import { PIX_ACTIONS, transitoryPixPage } from './pix.screen';
import { TRANSFER_ACTIONS, transitoryTransfersPage } from './transfer.screen';
import { bankAccountListFilters } from './bank-account.filters';
import {
  BANK_ACCOUNT_LIST_PATH,
  BANK_ACCOUNT_TAB_IDS,
  BANK_ACCOUNT_TABS,
  bankAccountPath,
  STATEMENT_PERIOD_IDS,
  TRANSITORY_TAB_IDS,
  TRANSITORY_TABS,
} from './bank-account.search';

// Manifestos de Contas digitais (U23/U42). Leve: o fio entra pela tela. As ações presas à conta aberta
// (lançamento, encerrar, reativar, atribuir, informe e exportações) recusam fora do detalhe; a tela troca
// `run` e `preview` pelos da conta da rota (`screens/bank-account-agent.ts`). Ação com step-up só prepara (U24).

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_ACCOUNT_ONLY = 'This action only runs on the bank account detail screen, on the account that is open there.';
const needsOpenAccount = () => {
  throw new Error(OPEN_ACCOUNT_ONLY);
};

const READ = can('BankAccount', 'Read');
const tabInput = (ids: readonly [string, ...string[]]) => z.object({ tab: z.enum(ids) });

// ─── lista ─────────────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`BankAccountHeader.tsx:30-40`): a lista e os saldos (`BankAccountListContainer.tsx:29-32`). */
export const bankAccountRefresh = action({
  id: 'bankAccount.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de contas e o saldo de cada linha. Nada é alterado.`,
  run: () => undefined,
});

/** "Mostrar valores" / "Ocultar valores" (`ShowValuesButton.tsx`): só a tela, nada vai à API. */
export const bankAccountToggleValues = action({
  id: 'bankAccount.toggleValues',
  kind: 'read',
  label: msg`Mostrar/ocultar valores`,
  description: msg`Mostra ou esconde os valores em dinheiro (saldos) na tela. Só muda a exibição.`,
  run: () => undefined,
});

/** "Entrar na conta" e clique na linha (`BankAccountList.tsx:176-186, 226`). */
export const bankAccountOpen = action({
  id: 'bankAccount.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Entrar na conta`,
  description: msg`Abre uma conta digital pelo id: cabeçalho com o titular e o saldo, o extrato e as outras abas.`,
  input: z.object({ id: z.string().min(1).max(64), name: z.string().max(200).optional() }),
  run: ({ nav, input }) => nav.to(bankAccountPath(input.id)),
});

export const bankAccountFilter = filterAction('bankAccount.filter', bankAccountListFilters, {
  description: msg`Filtra a lista de contas: searchString (todos os campos), f_personName (titular), f_personRegistrationNumber (CPF/CNPJ, dígitos e letras), f_account (número da conta, só dígitos), f_type (EscrowAccount, PaymentAccount, EscrowPlusAccount, EscrowMulticreditorsAccount) e, só no correspondente raiz, isDeleted (true ou false). Filtro omitido é limpo.`,
});

export const bankAccountListPage = listPageAction('bankAccount.list.page');

export const bankAccountListScreen = defineScreen({
  id: 'bankAccount.list',
  path: BANK_ACCOUNT_LIST_PATH,
  title: msg`Contas digitais`,
  description: msg`Contas digitais do correspondente: lista paginada com filtros, saldo por linha (que pode ser escondido) e entrada em cada conta.`,
  permission: READ,
  place: { icon: 'landmark', section: 'bank-account' },
  actions: [bankAccountRefresh, bankAccountToggleValues, bankAccountOpen, bankAccountFilter, bankAccountListPage],
});

// ─── detalhe e conta transitória ───────────────────────────────────────────────────────────────────

/** "Atualizar valores" (`BankAccountViewActions.tsx:136-141`). */
export const bankAccountDetailRefresh = action({
  id: 'bankAccount.detail.refresh',
  kind: 'read',
  label: msg`Atualizar valores`,
  description: msg`Busca de novo o saldo e os dados da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

const tabAction = (id: string, ids: readonly [string, ...string[]]) => {
  const tabs = ids.join(', ');
  return action<{ tab: string }>({
    id,
    kind: 'navigate',
    label: msg`Trocar de aba`,
    description: msg`Troca a aba da conta. Abas: ${tabs}. Extrato (extrato), Operadores (operadores), Acesso ao aplicativo (acesso-ao-aplicativo), Favorecidos (favorecidos), Meus limites (meus-limites) e Credores (credores) já estão no novo UYZY; as outras mostram o aviso de em construção.`,
    input: tabInput(ids),
    run: ({ nav, input }) => nav.search({ tab: input.tab === ids[0] ? undefined : input.tab, page: undefined, size: undefined, sheet: undefined }),
  });
};

export const bankAccountTab = tabAction('bankAccount.detail.tab', BANK_ACCOUNT_TAB_IDS);
export const transitoryTab = tabAction('bankAccount.transitory.tab', TRANSITORY_TAB_IDS);

/** O período do extrato (`StatementFiltersContainer.tsx:36-74`): preset ou `definir` com início e fim (até 90 dias). */
export const statementPeriodInput = z.strictObject({
  period: z.enum(STATEMENT_PERIOD_IDS),
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

const statementPeriodAction = (id: string) =>
  action<z.infer<typeof statementPeriodInput>>({
    id,
    kind: 'navigate',
    label: msg`Período do extrato`,
    description: msg`Escolhe o período do extrato: hoje, 7, 15, 30, 60 ou 90 (últimos N dias), ou definir com início e fim (datas civis AAAA-MM-DD, no máximo 90 dias de distância). Volta para a primeira página.`,
    input: statementPeriodInput,
    run: ({ nav, input }) => {
      if (input.period !== 'definir') return nav.search({ periodo: input.period === 'hoje' ? undefined : input.period, inicio: undefined, fim: undefined, page: undefined });
      if (!input.start || !input.end || input.start > input.end) throw new Error('definir needs start and end (YYYY-MM-DD), start not after end.');
      const days = (Date.parse(`${input.end}T00:00:00Z`) - Date.parse(`${input.start}T00:00:00Z`)) / 86_400_000;
      if (days > 90) throw new Error(`The period has ${String(days)} days; the limit is 90 days. Nothing changed.`);
      return nav.search({ periodo: 'definir', inicio: input.start, fim: input.end, page: undefined });
    },
  });

const statementDateTypeAction = (id: string) =>
  action<{ dateType: 'OccurrenceDate' | 'AccountingDate' }>({
    id,
    kind: 'navigate',
    label: msg`Tipo de data do extrato`,
    description: msg`Filtra o extrato pela data do lançamento (OccurrenceDate, o padrão) ou pela data contábil (AccountingDate).`,
    input: z.strictObject({ dateType: z.enum(['OccurrenceDate', 'AccountingDate']) }),
    run: ({ nav, input }) => nav.search({ data: input.dateType === 'AccountingDate' ? 'contabil' : undefined, page: undefined }),
  });

export const statementPeriod = statementPeriodAction('bankAccount.statement.period');
export const statementDateType = statementDateTypeAction('bankAccount.statement.dateType');
export const statementPage = listPageAction('bankAccount.statement.page');
export const transitoryStatementPeriod = statementPeriodAction('bankAccount.transitory.period');
export const transitoryStatementDateType = statementDateTypeAction('bankAccount.transitory.dateType');
export const transitoryStatementPage = listPageAction('bankAccount.transitory.page');

/** "Atualizar" do extrato (`AccountStatementHeader.tsx:24-28`): o extrato e o saldo da conta. */
export const statementRefresh = action({
  id: 'bankAccount.statement.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o extrato e o saldo da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

const openAccountRead = (id: string, label: MessageDescriptor, description: MessageDescriptor) =>
  action({ id, kind: 'read', label, description, input: z.strictObject({}), run: needsOpenAccount });

export const statementExportPdf = openAccountRead('bankAccount.statement.exportPdf', msg`Exportar PDF`, msg`Abre o PDF do extrato da conta aberta, no período e no tipo de data da tela. Não recebe entrada.`);
export const statementExportOfx = openAccountRead('bankAccount.statement.exportOfx', msg`Exportar OFX`, msg`Abre o arquivo OFX do extrato da conta aberta, no período da tela. Não recebe entrada.`);
export const statementExportExcel = openAccountRead('bankAccount.statement.exportExcel', msg`Exportar Excel`, msg`Baixa a planilha (CSV) do extrato da conta aberta, no período da tela. Só quando o extrato tem lançamentos. Não recebe entrada.`);
export const statementExportCnab240 = openAccountRead('bankAccount.statement.exportCnab240', msg`Exportar CNAB240`, msg`Gera o arquivo CNAB240 da conta aberta, no período da tela, e abre o link temporário dele. Só quando o extrato tem lançamentos. Não recebe entrada.`);
export const bankAccountIncomeTax = openAccountRead('bankAccount.incomeTax', msg`Baixar informe de rendimentos`, msg`Abre o PDF do informe de rendimentos da conta aberta. Não recebe entrada.`);

/**
 * "Encerrar conta" (`BankAccountViewActions.tsx:114-119`, popup de `BankAccountViewContainer.tsx:177-191`):
 * raiz e `TerminateAccount`. Presa à conta aberta: a entrada é só o motivo, a tela troca `run` e `preview`.
 */
export const bankAccountTerminate = action({
  id: 'bankAccount.terminate',
  kind: 'destructive',
  label: msg`Encerrar conta`,
  description: msg`Encerra a conta digital aberta na tela, com o motivo (de 3 a 200 caracteres). A conta é sempre a aberta e não pode ser escolhida. Só para o correspondente raiz. Depois de encerrar, a tela volta para a lista de contas.`,
  permission: can('BankAccount', 'TerminateAccount', { strict: true }),
  // Sem aparar: o corpo é o mesmo que o formulário mandaria (`TerminateSchema.ts:3-11`).
  input: z.strictObject({ reason: z.string().min(3).max(200) }),
  preview: () => ({ title: i18n._(msg`Encerrar conta`), detail: i18n._(msg`Tem certeza que deseja encerrar a conta?`), tone: 'destructive' }),
  run: needsOpenAccount,
});

/** "Reativar conta" (`BankAccountViewForm.tsx:138-150`, popup de `BankAccountViewContainer.tsx:193-210`): conta encerrada, raiz e `ReactivateAccount`. */
export const bankAccountReactivate = action({
  id: 'bankAccount.reactivate',
  kind: 'write',
  label: msg`Reativar conta`,
  description: msg`Reativa a conta digital encerrada que está aberta na tela. Não recebe entrada: a conta é sempre a aberta. Só para o correspondente raiz.`,
  permission: can('BankAccount', 'ReactivateAccount', { strict: true }),
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Reativar conta`), detail: i18n._(msg`Tem certeza que deseja reativar a conta?`) }),
  run: needsOpenAccount,
});

/**
 * "Adicionar Lançamento" (`BankAccountViewActions.tsx:108-113`, drawer de `BankAccountViewContainer.tsx:146-163`):
 * raiz e `AddAccountEntry`. Pede senha e código MFA (F2a): a ferramenta **só prepara** (U24), deixando o
 * formulário aberto na conta da rota, com o rascunho que o agente mandou. Quem confirma é a pessoa.
 */
export const bankAccountAddEntry = action({
  id: 'bankAccount.addEntry',
  kind: 'write',
  stepUp: true,
  label: msg`Adicionar Lançamento`,
  description: msg`Abre o formulário de lançamento manual na conta aberta na tela, já preenchido com o que vier: valor em reais com ponto decimal (amount, por exemplo 1234.56), documento (até 20 caracteres), complemento (até 25) e tipo de lançamento (entryType). A conta é sempre a aberta e não pode ser escolhida. Só para o correspondente raiz.`,
  permission: can('BankAccount', 'AddAccountEntry', { strict: true }),
  input: addEntryDraftInput,
  preview: () => ({ title: i18n._(msg`Adicionar Lançamento`) }),
  run: needsOpenAccount,
});

/** Quem recebe a conta: operador (`operatorName` ou `operatorId`) ou grupo (`groupName`), se preciso de outro correspondente (`tenant`). */
export const bankAccountAssignInput = z.strictObject({
  operatorName: z.string().min(1).max(200).optional(),
  operatorId: z.string().min(1).max(200).optional(),
  groupName: z.string().min(1).max(200).optional(),
  tenant: z.string().min(1).max(64).optional(),
});

/**
 * "Atribuir" (`BankAccountViewActions.tsx:142-147`, `AssignDrawer recordType='BankAccount'` em
 * `BankAccountViewContainer.tsx:212-219`): raiz **ou** `AssignRecord`, regra que a permissão do manifesto
 * não expressa; a tela esconde a ação quando a barra não a mostra. Presa à conta aberta.
 */
export const bankAccountAssign = action({
  id: 'bankAccount.assign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Atribui a conta aberta na tela a um operador ou a um grupo (groupName), se preciso de outro correspondente (tenant). O operador vai pelo nome (operatorName, como aparece no campo Operadores) ou, no correspondente atual, pelo id (operatorId). A conta é sempre a aberta e não pode ser escolhida. Pede a confirmação da pessoa.`,
  input: bankAccountAssignInput,
  preview: () => ({ title: i18n._(msg`Atribuir`), detail: i18n._(msg`Selecione um correspondente, operador ou grupo`) }),
  run: needsOpenAccount,
});

export const bankAccountDetailScreen = defineScreen({
  id: 'bankAccount.detail',
  path: `${BANK_ACCOUNT_LIST_PATH}/$bankAccountId`,
  title: msg`Conta Digital`,
  description: msg`Uma conta digital: cabeçalho com o titular, dados bancários, saldo disponível, bloqueado e total e tarifas pendentes; ações (adicionar lançamento, atualizar, encerrar, reativar, atribuir, informe de rendimentos); a aba Extrato (período, tipo de data, exportações em PDF, OFX, Excel e CNAB240), a aba Transferências (lista de TEDs, nova TED, aprovar, reprovar, cancelar, comprovante e exportação), a aba Pix (Transferir por Pix, Copia e cola, Receber Pix QR Code, Gestão de chaves e a mesma lista com aprovar, reprovar, cancelar, comprovante e exportação), a aba Pagamentos (pagar boleto e conta de consumo, a lista de pagamentos com aprovar, reprovar, cancelar, comprovante, copiar o código de barras e exportação), as abas Operadores, Acesso ao aplicativo, Favorecidos (lista, aprovar e reprovar), Meus limites (limites atuais e pedido de aumento) e Credores, e o aviso de em construção nas abas que ainda não migraram.`,
  permission: READ,
  place: { icon: 'landmark', parent: 'bankAccount.list', record: msg`Conta Digital`, tabs: BANK_ACCOUNT_TABS.map(({ id, label, icon }) => ({ id, label, icon })) },
  actions: [
    bankAccountDetailRefresh,
    bankAccountToggleValues,
    bankAccountTab,
    bankAccountAddEntry,
    bankAccountTerminate,
    bankAccountReactivate,
    bankAccountAssign,
    bankAccountIncomeTax,
    statementRefresh,
    statementPeriod,
    statementDateType,
    statementPage,
    statementExportPdf,
    statementExportOfx,
    statementExportExcel,
    statementExportCnab240,
    ...BANK_ACCOUNT_TAB_ACTIONS,
    ...BENEFICIARY_LIMIT_ACTIONS,
    ...TRANSFER_ACTIONS,
    ...PIX_ACTIONS,
    ...PAYMENT_ACTIONS,
  ],
});

/**
 * Conta transitória somente leitura (`router.tsx:488-519`, `readOnly` por `/visualizar`): sem ações, sem
 * informe, sem tarifas pendentes e sem exportações. `Read` **ou** `ViewTransitoryAccount`; a seção é a
 * da lista, sem ser filha dela, porque a lista pede `Read`.
 */
export const transitoryAccountScreen = defineScreen({
  id: 'bankAccount.transitory',
  path: `${BANK_ACCOUNT_LIST_PATH}/$bankAccountId/visualizar`,
  title: msg`Visualizar Conta`,
  description: msg`Visão somente leitura de uma conta digital transitória (aberta a partir de uma operação de crédito): titular, dados bancários, saldo disponível, o extrato, as listas de transferências (TED e Pix) e a de pagamentos, sem as ações da conta nem o cabeçalho de pagar.`,
  access: { anyOf: [READ, can('BankAccount', 'ViewTransitoryAccount')] },
  place: { icon: 'landmark', section: 'bank-account', record: msg`Visualizar Conta`, tabs: TRANSITORY_TABS.map(({ id, label, icon }) => ({ id, label, icon })) },
  actions: [transitoryTab, transitoryStatementPeriod, transitoryStatementDateType, transitoryStatementPage, transitoryTransfersPage, transitoryPixPage, transitoryPaymentsPage],
});
